/**
 * Portfolio Team Hours Table Component
 * Shows planned vs actual hours rolled up by Engagement Lead, with Branch,
 * # Engagements, Billable %, Non-Billable %, and Avg Utilization columns.
 * Pagination (8 rows/page) and sorting via shared DataTable component.
 */

import { useMemo } from 'react'
import type { PortfolioTeamHoursResponse, EngagementLeadHours } from '@/types/ui/dashboard'
import { cn } from '@/lib/utils'
import { useFormatters } from '@/hooks/use-formatters'
import { DataTable, type ColumnDef } from '@/components/ui/data-table'

interface PortfolioTeamHoursTableProps {
  data: PortfolioTeamHoursResponse | null
  isLoading?: boolean
  error?: string | null
}

const getVarianceColor = (v: number) => {
  if (v > 0) return 'text-green-600'
  if (v < 0) return 'text-red-600'
  return ''
}

const utilizationColor = (pct: number) => {
  if (pct >= 85) return 'text-green-600'
  if (pct >= 70) return 'text-amber-600'
  return 'text-red-600'
}

const utilizationBarColor = (pct: number) => {
  if (pct >= 85) return 'bg-green-500'
  if (pct >= 70) return 'bg-amber-500'
  return 'bg-red-500'
}

export function PortfolioTeamHoursTable({ data, isLoading, error }: PortfolioTeamHoursTableProps) {
  const { formatDecimal, formatPercent } = useFormatters()

  const totals = useMemo(() => {
    const leads = data?.leads
    if (!leads || leads.length === 0) return null
    const totalPlanned = leads.reduce((s, l) => s + l.planned, 0)
    const totalActual = leads.reduce((s, l) => s + l.actual, 0)
    const totalBillable = leads.reduce((s, l) => s + l.billable, 0)
    const totalNonBillable = leads.reduce((s, l) => s + l.nonBillable, 0)
    return {
      engagementCount: leads.reduce((s, l) => s + l.engagementCount, 0),
      planned: totalPlanned,
      actual: totalActual,
      variancePercent: totalPlanned > 0 ? ((totalActual - totalPlanned) / totalPlanned) * 100 : 0,
      billable: totalBillable,
      billablePercent: totalActual > 0 ? (totalBillable / totalActual) * 100 : 0,
      nonBillable: totalNonBillable,
      nonBillablePercent: totalActual > 0 ? (totalNonBillable / totalActual) * 100 : 0,
      avgUtilizationPercent: totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0,
    }
  }, [data])

  const columns: ColumnDef<EngagementLeadHours>[] = [
    {
      key: 'leadName',
      header: 'Engagement Lead',
      sortable: true,
      sortKey: 'leadName',
      render: row => <span className="whitespace-nowrap">{row.leadName}</span>,
    },
    {
      key: 'branch',
      header: 'Branch',
      sortable: true,
      sortKey: 'branch',
      render: row => <span className="text-muted-foreground whitespace-nowrap">{row.branch}</span>,
    },
    {
      key: 'engagementCount',
      header: '# Engagements',
      align: 'right',
      sortable: true,
      sortKey: 'engagementCount',
      render: row => row.engagementCount,
      footerRender: () => totals?.engagementCount,
    },
    {
      key: 'planned',
      header: 'Planned Hrs',
      align: 'right',
      sortable: true,
      sortKey: 'planned',
      render: row => formatDecimal(row.planned, 0),
      footerRender: () => totals ? formatDecimal(totals.planned, 0) : null,
    },
    {
      key: 'actual',
      header: 'Actual Hrs',
      align: 'right',
      sortable: true,
      sortKey: 'actual',
      render: row => formatDecimal(row.actual, 0),
      footerRender: () => totals ? formatDecimal(totals.actual, 0) : null,
    },
    {
      key: 'variancePercent',
      header: 'Variance',
      align: 'right',
      sortable: true,
      sortKey: 'variancePercent',
      render: row => (
        <span className={cn('whitespace-nowrap', getVarianceColor(row.variancePercent))}>
          {row.variancePercent >= 0 ? '+' : ''}{formatPercent(row.variancePercent, 1)}
        </span>
      ),
      footerRender: () => totals ? (
        <span className={cn('whitespace-nowrap', getVarianceColor(totals.variancePercent))}>
          {totals.variancePercent >= 0 ? '+' : ''}{formatPercent(totals.variancePercent, 1)}
        </span>
      ) : null,
    },
    {
      key: 'billable',
      header: 'Billable Hrs',
      align: 'right',
      sortable: true,
      sortKey: 'billable',
      render: row => formatDecimal(row.billable, 0),
      footerRender: () => totals ? formatDecimal(totals.billable, 0) : null,
    },
    {
      key: 'billablePercent',
      header: 'Billable %',
      align: 'right',
      sortable: true,
      sortKey: 'billablePercent',
      render: row => formatPercent(row.billablePercent, 1),
      footerRender: () => totals ? formatPercent(totals.billablePercent, 1) : null,
    },
    {
      key: 'nonBillable',
      header: 'Non-Billable Hrs',
      align: 'right',
      sortable: true,
      sortKey: 'nonBillable',
      render: row => formatDecimal(row.nonBillable, 0),
      footerRender: () => totals ? formatDecimal(totals.nonBillable, 0) : null,
    },
    {
      key: 'nonBillablePercent',
      header: 'Non-Billable %',
      align: 'right',
      sortable: true,
      sortKey: 'nonBillablePercent',
      render: row => formatPercent(row.nonBillablePercent, 1),
      footerRender: () => totals ? formatPercent(totals.nonBillablePercent, 1) : null,
    },
    {
      key: 'avgUtilizationPercent',
      header: 'Avg Utilization',
      align: 'right',
      sortable: true,
      sortKey: 'avgUtilizationPercent',
      render: row => (
        <div className="flex items-center justify-end gap-2">
          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full', utilizationBarColor(row.avgUtilizationPercent))}
              style={{ width: `${Math.min(row.avgUtilizationPercent, 100)}%` }}
            />
          </div>
          <span className={cn('tabular-nums whitespace-nowrap', utilizationColor(row.avgUtilizationPercent))}>
            {formatPercent(row.avgUtilizationPercent, 1)}
          </span>
        </div>
      ),
      footerRender: () => totals ? (
        <div className="flex items-center justify-end gap-2">
          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full', utilizationBarColor(totals.avgUtilizationPercent))}
              style={{ width: `${Math.min(totals.avgUtilizationPercent, 100)}%` }}
            />
          </div>
          <span className={cn('tabular-nums whitespace-nowrap', utilizationColor(totals.avgUtilizationPercent))}>
            {formatPercent(totals.avgUtilizationPercent, 1)}
          </span>
        </div>
      ) : null,
    },
  ]

  return (
    <DataTable<EngagementLeadHours>
      id="portfolio-team-hours-table"
      title="Team Hours Summary — by Engagement Lead"
      description="Planned vs Actual hours rolled up across all engagements per lead"
      columns={columns}
      data={data?.leads ?? null}
      getRowKey={(row) => `${row.leadName}:${row.branch}`}
      isLoading={isLoading}
      error={error}
      emptyMessage="No team hours data available"
      showFooter={!!totals}
      footerColSpan={2}
      exportConfig={{
        filenamePrefix: 'PortfolioTeamHours',
        sheetName: 'Team Hours by Lead',
        buildExportData: () =>
          (data?.leads ?? []).map(l => ({
            'Engagement Lead': l.leadName,
            Branch: l.branch,
            '# Engagements': l.engagementCount,
            'Planned Hrs': l.planned,
            'Actual Hrs': l.actual,
            'Variance %': l.variancePercent,
            'Billable Hrs': l.billable,
            'Billable %': l.billablePercent,
            'Non-Billable Hrs': l.nonBillable,
            'Non-Billable %': l.nonBillablePercent,
            'Avg Utilization %': l.avgUtilizationPercent,
          })),
      }}
    />
  )
}
